import type {
  BookingRecordModel,
  BookingStatusModel,
  BookingSubmissionModel,
} from "../Models/BookingModels";
import {
  AppService,
  safeJsonParseFn,
  safeJsonStringifyFn,
  type HttpClientModel,
  type Result,
} from "./App.service";
import { buildBookingRecordFn } from "../../Features/Booking/Functions";

const bookingStorageKey = "cmj-cinema:bookings";

const readBookingsFromStorageFn = (): BookingRecordModel[] => {
  if (typeof window === "undefined") {
    return [];
  }

  return (
    safeJsonParseFn<BookingRecordModel[]>(
      window.localStorage.getItem(bookingStorageKey)
    ) ?? []
  );
};

const writeBookingsToStorageFn = (bookings: BookingRecordModel[]) => {
  if (typeof window === "undefined") {
    return;
  }

  const serialized = safeJsonStringifyFn(bookings);
  if (serialized) {
    window.localStorage.setItem(bookingStorageKey, serialized);
  }
};

const upsertBookingFn = (booking: BookingRecordModel) => {
  const currentBookings = readBookingsFromStorageFn();
  const nextBookings = [
    booking,
    ...currentBookings.filter((item) => item.id !== booking.id),
  ];
  writeBookingsToStorageFn(nextBookings);
};

export const BookingService = {
  async createBooking(
    submission: BookingSubmissionModel,
    client: HttpClientModel = AppService.httpClient
  ): Promise<Result<BookingRecordModel>> {
    const localBooking = buildBookingRecordFn(submission);
    upsertBookingFn(localBooking);

    try {
      const response = await client.post("/api/bookings", localBooking);
      const apiBooking = {
        ...localBooking,
        ...(response.data as Partial<BookingRecordModel>),
        source: "api" as const,
        updatedAt: new Date().toISOString(),
      };
      upsertBookingFn(apiBooking);
      return { ok: true, data: apiBooking };
    } catch {
      return { ok: false, error: "We could not confirm your booking request reached the studio. Please contact us directly before trying again." };
    }
  },

  getBookingById(bookingId: string) {
    return readBookingsFromStorageFn().find((item) => item.id === bookingId) ?? null;
  },

  listBookings() {
    return readBookingsFromStorageFn();
  },

  updateBookingStatus(bookingId: string, status: BookingStatusModel) {
    const booking = this.getBookingById(bookingId);
    if (!booking) {
      return null;
    }

    const nextBooking = {
      ...booking,
      status,
      updatedAt: new Date().toISOString(),
    };
    upsertBookingFn(nextBooking);
    return nextBooking;
  },
};

export default BookingService;
